import { array } from '@/utils';
import { LookupOption } from './lookupOption';
import { useCallback, useEffect, useReducer } from 'react';

export interface LookupSource {
  searchField?: string;
  search: (searchString: string | undefined) => Promise<LookupOption[]>;
  initial: (value: string) => Promise<LookupOption>;
  onNewValue?: (search: string) => LookupOption;
}

interface EntireLoadConfig {
  load: () => Promise<LookupOption[]>;
  initial: (value: string) => Promise<LookupOption>;
}

export const EMPTY_LOOKUP_SOURCE: LookupSource = {
  initial: () => Promise.resolve(null!),
  search: () => Promise.resolve(array.empty<LookupOption>()),
};

export function createEntireLoadLookupSource(
  config: EntireLoadConfig,
): LookupSource {
  const source = new EntireLoadLookupSource(config);
  return { initial: source.initial, search: source.search };
}

class EntireLoadLookupSource implements LookupSource {
  private _config: EntireLoadConfig;
  private _source: Promise<LookupOption[]>;
  private _resolve!: (value: LookupOption[]) => any;
  private _resolved: boolean = false;
  private _fetching: boolean = false;

  constructor(config: EntireLoadConfig) {
    this._config = config;

    this._source = new Promise((resolve) => {
      this._resolve = resolve;
    });
  }

  public search = async (searchString: string | undefined) => {
    if (this._resolved || this._fetching) {
      return await this._source;
    }

    this._fetching = true;
    this._config
      .load()
      .then(this._resolve)
      .finally(() => (this._fetching = false));

    return this._source;
  };

  public initial = (value: string) => {
    return this._config.initial(value);
  };
}

interface AsyncLookupState {
  isLoading: boolean;
  data: LookupOption[];
}

type AsyncLookupAction =
  | { type: 'load/start' }
  | { type: 'load/success'; payload: LookupOption[] }
  | { type: 'load/error' };

function asyncLookupReducer(
  state: AsyncLookupState,
  action: AsyncLookupAction,
): AsyncLookupState {
  switch (action.type) {
    case 'load/start':
      return { ...state, isLoading: true };

    case 'load/success':
      return { isLoading: false, data: action.payload ?? [] };

    case 'load/error':
      return { isLoading: false, data: [] };

    default:
      return state;
  }
}

export function useAsyncLookup(
  dataSource: LookupSource,
  options?: {
    autoLoad?: boolean;
  },
) {
  const { search } = dataSource;

  const [state, dispatch] = useReducer(asyncLookupReducer, {
    isLoading: false,
    data: [],
  });

  const load = useCallback(
    async (searchValue?: string) => {
      console.log('LOAD CALLED', searchValue);

      dispatch({ type: 'load/start' });

      try {
        const result = await search(searchValue);
        dispatch({ type: 'load/success', payload: result ?? [] });
      } catch {
        dispatch({ type: 'load/error' });
      }
    },
    [search],
  );

  const refetch = useCallback(() => load(undefined), [load]);

  useEffect(() => {
    if (options?.autoLoad) {
      load(undefined);
    }
  }, [options?.autoLoad, load]);

  return {
    data: state.data,
    isLoading: state.isLoading,
    load,
    refetch,
  };
}
